import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { Slider } from '../../shared/slider';
import { copyTradingSettingsBunja } from '../../state';

export const TotalStopLossSection = () => {
  const { totalStopLossAtom, triggerBalanceAtom, estimatedPnlAtom } = useBunja(copyTradingSettingsBunja);
  const [totalStopLoss, setTotalStopLoss] = useAtom(totalStopLossAtom);
  const triggerBalance = useAtomValue(triggerBalanceAtom);
  const estimatedPnl = useAtomValue(estimatedPnlAtom);
  return (
    <div className={stack({ gap: 8 })}>
      <div className={hstack({ gap: 4 })}>
        <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
          <T id='Copy.r260126.Stop_loss' />
        </p>
        <Tooltip.Root placement='right'>
          <Tooltip.Trigger>
            <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
          </Tooltip.Trigger>
          <Tooltip.Content bottomSheetTitle={<T id='Copy.r260126.Stop_loss' />}>
            <T id='Copy.r260126.Reaching_this_level_triggers_a_stop_loss_process_to_close_positions' />
          </Tooltip.Content>
        </Tooltip.Root>
      </div>
      <div className={flex({ align: 'flex-end', gap: 2, color: 'content.default.level-5' })}>
        <p className={css({ textStyle: 'Title/M/Monospace' })}>{totalStopLoss.toString()}</p>
        <p className={css({ textStyle: 'Text/L/Monospace/Strong' })}>%</p>
      </div>
      <div className={stack({ gap: 16 })}>
        <Slider
          value={totalStopLoss.toNumber()}
          onChange={(value) => setTotalStopLoss(Num(value))}
          min={5}
          max={95}
          step={1}
          marks={[5, 25, 50, 75, 95]}
          getLabel={(mark) => `${mark}%`}
        />
        <div className={stack({ gap: 4 })}>
          <div className={stack({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            <div className={hstack({ gap: 2 })}>
              <p>
                <T id='Copy.r260126.Trigger_balance' />:
              </p>
              {triggerBalance ? (
                <Formatted.Number
                  value={triggerBalance}
                  toFixed
                  precision={UsdtPrecision}
                  suffix={UsdtCurrency}
                  css={{ textStyle: 'Text/M/Monospace/Strong' }}
                />
              ) : (
                <p className={css({ textStyle: 'Text/M/Monospace/Strong' })}>-</p>
              )}
            </div>
            <div className={hstack({ gap: 2 })}>
              <p>
                <T id='Copy.r260126.Est_pnl' />
              </p>
              {estimatedPnl ? (
                <Formatted.Number
                  value={estimatedPnl}
                  toFixed
                  precision={UsdtPrecision}
                  suffix={UsdtCurrency}
                  css={{ textStyle: 'Text/M/Monospace/Strong' }}
                />
              ) : (
                <p className={css({ textStyle: 'Text/M/Monospace/Strong' })}>-</p>
              )}
            </div>
          </div>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
            <T id='Copy.r260126.Deposit_or_withdrawal_will_recalculate_the_stop_loss_balance' />
          </p>
        </div>
      </div>
    </div>
  );
};
